import React, { useRef } from "react";
import { Card, Skeleton } from "antd";
import { colorForScore } from "../constants/npsConstants";
import NPSAnimatedEmoji from "./NPSAnimatedEmoji";
import NPSCardHead from "./NPSCardHead";
import "./NPSScoreDistribution.css";

// Deterministic placeholder heights so the skeleton bars don't render flat.
const SKELETON_HEIGHTS = [35, 60, 25, 70, 45, 90, 55, 30, 65, 80, 40];

const DistributionBar = ({ score, count, max, onSelect }) => {
  const emojiRef = useRef(null);

  return (
    <button
      type="button"
      className="nps-bar-col"
      onClick={() => onSelect(score)}
      onMouseEnter={() => emojiRef.current?.play()}
      onFocus={() => emojiRef.current?.play()}
      aria-label={`Score ${score}: ${count} responses`}
    >
      <NPSAnimatedEmoji ref={emojiRef} score={score} size={26} />
      <span className="nps-bar-count">{count}</span>
      <span className="nps-bar-track">
        <span
          className="nps-bar-fill"
          style={{ height: `${Math.max(4, (count / max) * 100)}%`, background: colorForScore(score) }}
        />
      </span>
      <span className="nps-bar-num">{score}</span>
    </button>
  );
};

const NPSScoreDistribution = ({ distribution, total, onSelectScore, promoters, passives, detractors, isLoading }) => {
  const max = Math.max(...Object.values(distribution));

  if (isLoading) {
    return (
      <Card className="nps-card nps-dist-card" bodyStyle={{ padding: 0 }}>
        <NPSCardHead title="Score distribution" note="Loading responses…" />
        <div className="nps-card-body">
          <div className="nps-bars">
            {SKELETON_HEIGHTS.map((height, score) => (
              <div className="nps-bar-col" key={score} style={{ cursor: "default" }}>
                <Skeleton.Avatar active shape="circle" size={26} />
                <span className="nps-bar-track">
                  <span className="nps-bar-fill" style={{ height: `${height}%`, background: "#eef1f5" }} />
                </span>
                <Skeleton.Input active size="small" style={{ width: 14 }} />
              </div>
            ))}
          </div>
          <div className="nps-zones">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton.Input active size="small" key={i} style={{ width: "100%" }} />
            ))}
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card className="nps-card nps-dist-card" bodyStyle={{ padding: 0 }}>
      <NPSCardHead
        title="Score distribution"
        note={`${total.toLocaleString()} responses`}
      />
      <div className="nps-card-body">
        <div className="nps-bars">
          {Array.from({ length: 11 }, (_, score) => (
            <DistributionBar
              key={score}
              score={score}
              count={distribution[score] || 0}
              max={max}
              onSelect={onSelectScore}
            />
          ))}
        </div>
        <div className="nps-zones">
          <div className="nps-zone nps-zone-detractor">Detractors <small>0–6 · {detractors}</small></div>
          <div className="nps-zone nps-zone-passive">Passives <small>7–8 · {passives}</small></div>
          <div className="nps-zone nps-zone-promoter">Promoters <small>9–10 · {promoters}</small></div>
        </div>
      </div>
    </Card>
  );
};

export default NPSScoreDistribution;
